import React, { useEffect, useState } from "react"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { CheckIcon } from "@heroicons/react/20/solid"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { TAbrandUserCategories } from "../../../../services/userAPI"

interface SubCategory {
  category: string
  count: number
}

interface Category {
  main_category: string
  sub_categories: SubCategory[]
}

interface CategoryModalProps {
  title: string
  isOpen: boolean
  onClose: () => void
  onSelect: (category: string) => void
  selectedValue?: { category?: string }
}

const CategoryModal: React.FC<CategoryModalProps> = ({
  title,
  isOpen,
  onClose,
  onSelect,
  selectedValue,
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [categories, setCategories] = useState<Category[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>("")

  useEffect(() => {
    if (isOpen) {
      fetchUserCategories()
      if (selectedValue?.category) {
        const firstCategory = selectedValue.category.split(",")[0].trim()
        setSelectedCategory(firstCategory)
      } else {
        setSelectedCategory("")
      }
    }
  }, [isOpen, selectedValue])

  const fetchUserCategories = async () => {
    const response = await TAbrandUserCategories()
    if (response && response.data) {
      setCategories(response.data)
    }
  }

  const filteredCategories = categories
    .map((category) => {
      const filteredSubs = category.sub_categories.filter((sub) =>
        sub.category.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      return { ...category, sub_categories: filteredSubs }
    })
    .filter((category) => category.sub_categories.length > 0)

  const handleCategorySelect = (subCategory: string) => {
    setSelectedCategory(subCategory)
  }

  const isSelected = (subCategory: string) => {
    return selectedCategory === subCategory
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">{title}</h2>
              <p className="text-sm opacity-90 mt-1">Kategori Seçimi</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Search Input */}
          <div className="relative w-full mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Kategori Ara"
              className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-2xl border-none focus:ring-0 focus:outline-none"
            />
          </div>

          {/* Categories List */}
          <div className="space-y-2">
            {filteredCategories.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">Kategori Bulunamadı</div>
            ) : (
              filteredCategories.map((category) => (
                <div key={category.main_category} className="space-y-2">
                  <h3 className="font-semibold text-gray-800 text-sm">{category.main_category}</h3>
                  <div className="space-y-1 ml-4">
                    {category.sub_categories.map((subCategory) => (
                      <div
                        key={subCategory.category}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleCategorySelect(subCategory.category)}
                      >
                        <span className="text-sm text-gray-700">{subCategory.category}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">({subCategory.count})</span>
                          {isSelected(subCategory.category) && (
                            <CheckIcon className="w-4 h-4 text-blue-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedCategory ? "1 kategori seçili" : "Kategori seçilmedi"}
            </div>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  if (selectedCategory) {
                    onSelect(selectedCategory)
                  }
                  onClose()
                }}
                disabled={!selectedCategory}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CategoryModal
